import React, { createContext, useContext, useRef, useCallback, useState, useEffect } from 'react';
import { Howl } from 'howler';

// Create the context
const HowlAudioContext = createContext();

// Custom hook to use the audio context
export const useHowlAudio = () => {
  const context = useContext(HowlAudioContext);
  if (!context) {
    throw new Error('useHowlAudio must be used within a HowlAudioProvider');
  }
  return context;
};

// Provider component
export const HowlAudioProvider = ({ children }) => {
  const audioInstancesRef = useRef(new Map());
  const [isPlaying, setIsPlaying] = useState(false);

  // Check if any audio instance is playing
  const checkAnyPlaying = useCallback(() => {
    let anyPlaying = false;
    audioInstancesRef.current.forEach((instance) => {
      if (instance.playing()) {
        anyPlaying = true;
      }
    });
    return anyPlaying;
  }, []);

  // Update global playing state
  const updatePlayingState = useCallback(() => {
    const anyPlaying = checkAnyPlaying();
    setIsPlaying(anyPlaying);
  }, [checkAnyPlaying]);

  // Create and play a Howl instance
  const playHowl = useCallback((audioConfig, instanceId = 'default') => {
    try {
      // Stop and cleanup existing instance if it exists
      if (audioInstancesRef.current.has(instanceId)) {
        const existingInstance = audioInstancesRef.current.get(instanceId);
        existingInstance.stop();
        existingInstance.unload();
        audioInstancesRef.current.delete(instanceId);
      }

      // Create new Howl instance
      const howlInstance = new Howl({
        src: audioConfig.src,
        volume: audioConfig.volume || 0.7,
        html5: audioConfig.html5 || true,
        loop: audioConfig.loop || false,
        preload: audioConfig.preload || true,
        onloaderror: function(id, err) {
          console.warn(`Audio failed to load for ${instanceId}:`, err);
          if (audioConfig.onloaderror) {
            audioConfig.onloaderror(id, err);
          }
        },
        onplayerror: function(id, err) {
          console.warn(`Audio failed to play for ${instanceId}:`, err);
          // Auto-retry on unlock
          howlInstance.once('unlock', function() {
            howlInstance.play();
          });
          if (audioConfig.onplayerror) {
            audioConfig.onplayerror(id, err);
          }
        },
        onplay: function() {
          updatePlayingState();
          if (audioConfig.onplay) {
            audioConfig.onplay();
          }
        },
        onend: function() {
          updatePlayingState();
          if (audioConfig.onend) {
            audioConfig.onend();
          }
        },
        onstop: function() {
          updatePlayingState();
          if (audioConfig.onstop) {
            audioConfig.onstop();
          }
        },
        onpause: function() {
          updatePlayingState();
          if (audioConfig.onpause) {
            audioConfig.onpause();
          }
        }
      });

      // Store the instance
      audioInstancesRef.current.set(instanceId, howlInstance);

      // Play the audio
      howlInstance.play();

      return howlInstance;
    } catch (error) {
      console.error(`Error creating Howl instance for ${instanceId}:`, error);
      if (audioConfig.onError) {
        audioConfig.onError(error);
      }
      return null;
    }
  }, [updatePlayingState]);

  // Stop a specific audio instance
  const stopHowl = useCallback((instanceId = 'default') => {
    if (audioInstancesRef.current.has(instanceId)) {
      const instance = audioInstancesRef.current.get(instanceId);
      instance.stop();
      updatePlayingState();
      return true;
    }
    return false;
  }, [updatePlayingState]);

  // Pause a specific audio instance
  const pauseHowl = useCallback((instanceId = 'default') => {
    if (audioInstancesRef.current.has(instanceId)) {
      const instance = audioInstancesRef.current.get(instanceId);
      instance.pause();
      updatePlayingState();
      return true;
    }
    return false;
  }, [updatePlayingState]);

  // Resume a specific audio instance
  const resumeHowl = useCallback((instanceId = 'default') => {
    if (audioInstancesRef.current.has(instanceId)) {
      const instance = audioInstancesRef.current.get(instanceId);
      instance.play();
      updatePlayingState();
      return true;
    }
    return false;
  }, [updatePlayingState]);

  // Stop and cleanup a specific audio instance
  const cleanupHowl = useCallback((instanceId = 'default') => {
    if (audioInstancesRef.current.has(instanceId)) {
      const instance = audioInstancesRef.current.get(instanceId);
      instance.stop();
      instance.unload();
      audioInstancesRef.current.delete(instanceId);
      updatePlayingState();
      return true;
    }
    return false;
  }, [updatePlayingState]);

  // Stop and cleanup all audio instances
  const cleanupAllHowl = useCallback(() => {
    audioInstancesRef.current.forEach((instance, instanceId) => {
      instance.stop();
      instance.unload();
    });
    audioInstancesRef.current.clear();
    updatePlayingState();
  }, [updatePlayingState]);

  // Get a specific audio instance
  const getHowlInstance = useCallback((instanceId = 'default') => {
    return audioInstancesRef.current.get(instanceId) || null;
  }, []);

  // Check if an audio instance is playing
  const isHowlPlaying = useCallback((instanceId = 'default') => {
    const instance = audioInstancesRef.current.get(instanceId);
    return instance ? instance.playing() : false;
  }, []);

  // Set volume for a specific instance
  const setHowlVolume = useCallback((volume, instanceId = 'default') => {
    if (audioInstancesRef.current.has(instanceId)) {
      const instance = audioInstancesRef.current.get(instanceId);
      instance.volume(volume);
      return true;
    }
    return false;
  }, []);

  // Get all active instance IDs
  const getActiveInstanceIds = useCallback(() => {
    return Array.from(audioInstancesRef.current.keys());
  }, []);

  // Check if any instance is playing
  const isAnyPlaying = useCallback(() => {
    return checkAnyPlaying();
  }, [checkAnyPlaying]);

  const playSound = useCallback((src, options = {}) => {
    const {
      volume = 0.7,
      loop = false,
      html5 = true,
      instanceId = `sound_${Date.now()}_${Math.random()}`, // Auto-generate unique ID
      onError = null,
      preload = true
    } = options;

    const audioConfig = {
      src: Array.isArray(src) ? src : [src],
      volume,
      loop,
      html5,
      preload,
      onError: (error) => {
        console.error("Error playing sound:", error);
        if (onError) onError(error);
      }
    };

    return playHowl(audioConfig, instanceId);
  }, [playHowl]);

  const contextValue = {
    playHowl,
    playSound,
    stopHowl,
    pauseHowl,
    resumeHowl,
    cleanupHowl,
    cleanupAllHowl,
    getHowlInstance,
    isHowlPlaying,
    setHowlVolume,
    getActiveInstanceIds,
    isPlaying,
    isAnyPlaying
  };

  return (
    <HowlAudioContext.Provider value={contextValue}>
      {children}
    </HowlAudioContext.Provider>
  );
};